import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const AdminTests = () => {
  const navigate = useNavigate()
  const { user, isAdmin, adminChecked } = useAuth()
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [archivedFilter, setArchivedFilter] = useState('active') // 'active' | 'archived' | 'all'
  const [archivingId, setArchivingId] = useState(null)
  
  // V1 Sorting & Search states
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('school_year') // default: sort by school year
  const [sortOrder, setSortOrder] = useState('desc') // desc = newest first

  // Aggregated per-test statistics (Phase 1: test-level only)
  const [statsByTestId, setStatsByTestId] = useState({})
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsTest, setStatsTest] = useState(null) // currently-open detail modal target

  useEffect(() => {
    if (!user || !adminChecked || !isAdmin) return
    fetchTests()
    fetchStatistics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminChecked, isAdmin, archivedFilter])

  const fetchStatistics = async () => {
    // ADMIN aggregate — endpoint is guarded by verify_admin on the backend.
    setStatsLoading(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const response = await fetch(`${BACKEND_URL}/api/admin/tests/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        const map = {}
        for (const row of (data.statistics || [])) {
          map[row.test_id] = row
        }
        setStatsByTestId(map)
      }
    } catch (_e) {
      // Non-fatal — the tests list still renders without stats.
    } finally {
      setStatsLoading(false)
    }
  }

  const formatPct = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(1).replace('.', ',')} %`)

  const fetchTests = async () => {
    setLoading(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(`${BACKEND_URL}/api/admin/tests?archived=${archivedFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setTests(data)
      } else {
        setError('Chyba pri načítaní testov')
      }
    } catch (err) {
      setError('Chyba pri načítaní testov')
    } finally {
      setLoading(false)
    }
  }

  const toggleArchive = async (test) => {
    const willArchive = !test.is_archived
    const msg = willArchive
      ? `Archivovať test „${test.title}"?\n\nArchivovaný test sa prestane zobrazovať užívateľom, ale zostane viditeľný pre adminov.`
      : `Obnoviť test „${test.title}" z archívu?\n\nPo obnovení bude opäť viditeľný pre užívateľov.`
    if (!window.confirm(msg)) return

    setArchivingId(test.id)
    setError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const path = willArchive ? 'archive' : 'unarchive'
      const res = await fetch(`${BACKEND_URL}/api/admin/tests/${test.id}/${path}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({}))
        throw new Error(e.detail || 'Chyba pri archivácii')
      }
      await fetchTests()
    } catch (err) {
      setError(err.message || 'Chyba pri archivácii')
    } finally {
      setArchivingId(null)
    }
  }

  // Filter tests by search query
  const filteredTests = tests.filter(test => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      test.title?.toLowerCase().includes(query) ||
      test.subject?.toLowerCase().includes(query) ||
      test.school_year?.includes(query)
    )
  })

  // Sort filtered tests
  const sortedTests = [...filteredTests].sort((a, b) => {
    let compareA, compareB
    
    switch (sortBy) {
      case 'name':
        compareA = a.title?.toLowerCase() || ''
        compareB = b.title?.toLowerCase() || ''
        break
      case 'school_year':
        compareA = a.school_year || ''
        compareB = b.school_year || ''
        break
      case 'subject':
        compareA = a.subject?.toLowerCase() || ''
        compareB = b.subject?.toLowerCase() || ''
        break
      case 'published':
        compareA = a.is_published ? 1 : 0
        compareB = b.is_published ? 1 : 0
        break
      default:
        return 0
    }
    
    if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1
    if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1
    return 0
  })

  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // New field - default to desc for school_year, asc for others
      setSortBy(field)
      setSortOrder(field === 'school_year' ? 'desc' : 'asc')
    }
  }

  if (!adminChecked || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam testy...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white py-6 mb-8">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Správa testov</h1>
            <p className="text-blue-100 mt-1">Všetky testy v systéme</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-lg transition"
          >
            ← Späť na Admin
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Zoznam testov ({sortedTests.length}{searchQuery && ` z ${tests.length}`})</h2>
            <button
              onClick={() => navigate('/admin/tests/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              + Vytvoriť nový test
            </button>
          </div>

          {/* Phase 4: Archive filter tabs */}
          <div className="flex gap-2 pb-4 border-b mb-1" data-testid="archive-filter-tabs">
            {[
              { key: 'active', label: 'Aktívne' },
              { key: 'archived', label: 'Archivované' },
              { key: 'all', label: 'Všetky' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setArchivedFilter(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded transition ${
                  archivedFilter === tab.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* V1 Search & Sort Controls */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            {/* Search Input */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="🔍 Hľadať test (názov, predmet, rok)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 whitespace-nowrap">Zoradiť:</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="school_year">Školský rok</option>
                <option value="name">Názov</option>
                <option value="subject">Predmet</option>
                <option value="published">Publikované</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
                title={sortOrder === 'asc' ? 'Vzostupne (A→Z, 0→9)' : 'Zostupne (Z→A, 9→0)'}
              >
                {sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
              </button>
            </div>
          </div>
        </div>

        {sortedTests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            {searchQuery ? (
              <>
                <p className="text-gray-500 text-lg mb-4">
                  Žiadne testy nezodpovedajú hľadaniu "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Zrušiť filter
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-500 text-lg mb-4">Zatiaľ nie sú vytvorené žiadne testy</p>
                <button
                  onClick={() => navigate('/admin/tests/new')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Vytvoriť prvý test
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Názov</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Školský rok</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Otázky</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publikovaný</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prístup</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedTests.map(test => (
                  <tr key={test.id} className={`hover:bg-gray-50 ${test.is_archived ? 'bg-gray-50 opacity-75' : ''}`} data-testid={`test-row-${test.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {test.title}
                        {test.is_archived && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded" data-testid={`archived-badge-${test.id}`}>
                            📦 Archivovaný
                          </span>
                        )}
                        {test.admin_only && (
                          <span
                            className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded"
                            data-testid={`admin-only-badge-${test.id}`}
                            title="Test je viditeľný len pre adminov (náhľad). Bežní používatelia ho neuvidia."
                          >
                            ADMIN náhľad
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{test.subject}</div>
                      {(() => {
                        const s = statsByTestId[test.id]
                        if (!s) return null
                        if (s.completed_attempts === 0) {
                          return (
                            <div
                              className="text-xs text-gray-400 mt-1"
                              data-testid={`test-stats-summary-${test.id}`}
                            >
                              Bez pokusov
                            </div>
                          )
                        }
                        return (
                          <div
                            className="text-xs text-gray-600 mt-1"
                            data-testid={`test-stats-summary-${test.id}`}
                          >
                            {s.unique_users} používateľov · {s.completed_attempts} dokončených pokusov · Ø {formatPct(s.avg_percentage)}
                          </div>
                        )
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{test.school_year}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{test.total_questions || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        test.is_published 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {test.is_published ? 'Áno' : 'Nie'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          test.is_free
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                        data-testid={`access-badge-${test.id}`}
                      >
                        {test.is_free ? 'Zadarmo' : 'Platený'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/admin/tests/${test.id}/questions`)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        Otázky
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => setStatsTest(test)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                        data-testid={`test-stats-btn-${test.id}`}
                      >
                        Štatistiky
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => navigate(`/admin/tests/${test.id}/edit`)}
                        className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                      >
                        Upraviť
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => toggleArchive(test)}
                        disabled={archivingId === test.id}
                        className={`font-medium text-sm disabled:opacity-40 ${test.is_archived ? 'text-green-700 hover:text-green-900' : 'text-amber-700 hover:text-amber-900'}`}
                        data-testid={`archive-toggle-${test.id}`}
                      >
                        {archivingId === test.id ? '…' : (test.is_archived ? 'Obnoviť' : 'Archivovať')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test statistics detail modal — Phase 1: aggregate only.
          No personal identifiers, no email list, no per-user history. */}
      {statsTest && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="test-stats-modal-title"
          data-testid="test-stats-modal"
          onClick={() => setStatsTest(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 id="test-stats-modal-title" className="text-xl font-bold text-gray-900">
                  Štatistiky testu
                </h2>
                <p className="text-sm text-gray-600 mt-1 truncate max-w-xs" title={statsTest.title}>
                  {statsTest.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStatsTest(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Zavrieť"
                data-testid="test-stats-modal-close"
              >
                ×
              </button>
            </div>

            {(() => {
              const s = statsByTestId[statsTest.id]
              if (statsLoading && !s) {
                return <p className="text-sm text-gray-500">Načítavam štatistiky…</p>
              }
              if (!s) {
                return <p className="text-sm text-gray-500">Štatistiky nie sú momentálne dostupné.</p>
              }
              const zero = s.completed_attempts === 0
              return (
                <dl className="grid grid-cols-2 gap-4">
                  <div className="col-span-1 rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <dt className="text-xs uppercase tracking-wide text-gray-500">Dokončené pokusy</dt>
                    <dd
                      className="text-2xl font-bold text-gray-900 mt-1"
                      data-testid="stats-completed-attempts"
                    >
                      {s.completed_attempts}
                    </dd>
                  </div>
                  <div className="col-span-1 rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <dt className="text-xs uppercase tracking-wide text-gray-500">Používatelia</dt>
                    <dd
                      className="text-2xl font-bold text-gray-900 mt-1"
                      data-testid="stats-unique-users"
                    >
                      {s.unique_users}
                    </dd>
                  </div>
                  <div className="col-span-1 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                    <dt className="text-xs uppercase tracking-wide text-emerald-800">Priemerná úspešnosť</dt>
                    <dd
                      className="text-2xl font-bold text-emerald-900 mt-1"
                      data-testid="stats-avg-percentage"
                    >
                      {zero ? '—' : formatPct(s.avg_percentage)}
                    </dd>
                  </div>
                  <div className="col-span-1 rounded-lg bg-blue-50 border border-blue-200 p-3">
                    <dt className="text-xs uppercase tracking-wide text-blue-800">Priemer prvého pokusu</dt>
                    <dd
                      className="text-2xl font-bold text-blue-900 mt-1"
                      data-testid="stats-first-attempt-avg"
                    >
                      {zero ? '—' : formatPct(s.first_attempt_avg)}
                    </dd>
                  </div>
                </dl>
              )
            })()}

            <p className="text-xs text-gray-500 mt-5">
              Údaje sú agregované z reálnych dokončených pokusov bežných používateľov. Pokusy admina sú z týchto štatistík vylúčené.
            </p>

            <div className="mt-5 text-right">
              <button
                type="button"
                onClick={() => setStatsTest(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
              >
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminTests
